// Structural types match the subset of Sentry's TransactionEvent / SpanJSON
// shape we read here. Defining them locally avoids depending on @sentry/core
// internal type re-exports, which @sentry/angular does not surface.
interface PerfSpan {
  description?: string;
  op?: string;
  start_timestamp?: number;
  timestamp?: number;
}

interface PerfTransactionEvent {
  contexts?: { trace?: { op?: string } };
  start_timestamp?: number;
  timestamp?: number;
  spans?: PerfSpan[];
}

type MeasurementUnit = 'millisecond' | 'none';
type Measurement = { value: number; unit: MeasurementUnit };
type Measurements = Record<string, Measurement>;

interface PerfAnnotations {
  tags: Record<string, string>;
  measurements: Measurements;
}

// Matches the new-survey backend method path in the span description, which
// the gRPC interceptor writes as `POST <apiUrl>/hmm.ninja.<Service>/<Method>`.
// Captures `<Service>` and `<Method>` separately.
const NINJA_METHOD_RE = /\/hmm\.ninja\.([A-Za-z0-9]+)\/([A-Za-z0-9]+)/;

/**
 * Derives tags + measurements that explain why a pageload / navigation
 * transaction has the duration it has, broken down across the entire
 * new-survey (`hmm.ninja.*`) RPC surface.
 *
 * Goal: in Sentry's Performance UI, filter to
 * `transaction.op:pageload perf.has_ninja_calls:yes`, then chart
 * `perf.duration_ms` against `perf.ninja_total_ms`, `perf.ninja_max_ms`,
 * and individual `perf.method.<name>_ms` to see whether the transaction
 * duration is driven by ninja RPC work, by the trim-to-last-child rule
 * extending the IdleTransaction across the post-Register chain, or by
 * something else entirely. `perf.slowest_ninja_method` and
 * `perf.last_ninja_method` name the specific RPC that dominates each
 * dimension.
 *
 * Returns empty annotations for any event that isn't a pageload or
 * navigation transaction.
 */
export function computePerfAnnotations(
  event: PerfTransactionEvent
): PerfAnnotations {
  const op = event.contexts?.trace?.op;
  if (op !== 'pageload' && op !== 'navigation') {
    return { tags: {}, measurements: {} };
  }

  const start = event.start_timestamp ?? 0;
  const end = event.timestamp ?? 0;
  const durationMs = (end - start) * 1000;

  const spans: PerfSpan[] = event.spans ?? [];

  const lastChildEndRelMs = spans.reduce((max: number, span: PerfSpan) => {
    const endRel = ((span.timestamp ?? 0) - start) * 1000;
    return endRel > max ? endRel : max;
  }, 0);

  // Walk every span exactly once, accumulating per-method totals and
  // ninja-namespace aggregates.
  const perMethodTotalMs: Record<string, number> = {};
  const perMethodCount: Record<string, number> = {};
  let ninjaTotalMs = 0;
  let ninjaCount = 0;
  let ninjaMaxMs = 0;
  let slowestNinjaMethod: string | undefined;
  let lastNinjaMethod: string | undefined;
  let lastNinjaEnd = -Infinity;

  for (const span of spans) {
    const ninjaMethod = extractNinjaMethod(span.description);
    if (ninjaMethod === undefined) continue;

    const spanMs = ((span.timestamp ?? 0) - (span.start_timestamp ?? 0)) * 1000;

    perMethodTotalMs[ninjaMethod] =
      (perMethodTotalMs[ninjaMethod] ?? 0) + spanMs;
    perMethodCount[ninjaMethod] = (perMethodCount[ninjaMethod] ?? 0) + 1;

    ninjaTotalMs += spanMs;
    ninjaCount += 1;

    if (spanMs > ninjaMaxMs) {
      ninjaMaxMs = spanMs;
      slowestNinjaMethod = ninjaMethod;
    }

    const spanEnd = span.timestamp ?? 0;
    if (spanEnd > lastNinjaEnd) {
      lastNinjaEnd = spanEnd;
      lastNinjaMethod = ninjaMethod;
    }
  }

  // perf.last_ninja_method and perf.slowest_ninja_method already give
  // us the actionable trim-point info; perf.last_span_desc was dropped
  // because span descriptions are URLs/dynamic strings whose cardinality
  // truncation does not bound (two distinct 60-char URLs produce two
  // distinct 50-char prefixes).
  const tags: Record<string, string> = {
    'perf.has_ninja_calls': ninjaCount > 0 ? 'yes' : 'no',
    'perf.span_count_bucket': bucketCount(spans.length),
    'perf.slowest_ninja_method': slowestNinjaMethod ?? 'none',
    'perf.last_ninja_method': lastNinjaMethod ?? 'none',
  };

  const measurements: Measurements = {
    'perf.duration_ms': { value: durationMs, unit: 'millisecond' },
    'perf.last_child_end_ms': {
      value: lastChildEndRelMs,
      unit: 'millisecond',
    },
    'perf.gap_to_last_child_ms': {
      value: durationMs - lastChildEndRelMs,
      unit: 'millisecond',
    },
    'perf.span_count': { value: spans.length, unit: 'none' },
    'perf.ninja_total_ms': { value: ninjaTotalMs, unit: 'millisecond' },
    'perf.ninja_count': { value: ninjaCount, unit: 'none' },
    'perf.ninja_max_ms': { value: ninjaMaxMs, unit: 'millisecond' },
  };

  for (const [method, totalMs] of Object.entries(perMethodTotalMs)) {
    measurements[`perf.method.${method}_ms`] = {
      value: totalMs,
      unit: 'millisecond',
    };
    measurements[`perf.method.${method}_count`] = {
      value: perMethodCount[method],
      unit: 'none',
    };
  }

  return { tags, measurements };
}

// Returns "<service>_<method>" in lower_snake_case, or undefined if the
// description doesn't match the hmm.ninja.* RPC URL shape. The normalised
// form keeps measurement keys filesystem- and dashboard-friendly while
// remaining unambiguous (e.g. NinjaService/Register → ninja_register,
// QuestionsService/QuestionResponse → questions_question_response).
function extractNinjaMethod(
  description: string | undefined
): string | undefined {
  if (description === undefined) return undefined;
  const match = NINJA_METHOD_RE.exec(description);
  if (match === null) return undefined;
  const service = match[1].replace(/Service$/, '');
  return `${camelToSnake(service)}_${camelToSnake(match[2])}`;
}

function camelToSnake(s: string): string {
  return s.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
}

function bucketCount(n: number): string {
  if (n < 5) return '<5';
  if (n < 20) return '5-19';
  if (n < 50) return '20-49';
  return '50+';
}
